import React, { useRef, useState } from 'react';
import { Row, flexRender } from '@tanstack/react-table';
import { StyledTableData, StyledTableDataDiv, StyledTableRow } from '../../../../shared/styles/components/table.styles';

import { ExpandedRowActions } from './ExpandedRowActions';
import { Item } from '../../types/item.types';
import SSRIcon from '@ingka/ssr-icon';
import { SubItem } from '../../types/sub-item.types';
import { css } from '@emotion/react';
import handIcon from '@ingka/ssr-icon/paths/hand';
import lockIcon from '@ingka/ssr-icon/paths/lock';
import styled from '@emotion/styled';
import { theme } from '../../../../shared/styles/core/theme';
import { useHover } from '@use-gesture/react';

interface IconProps {
   locked: boolean;
   paths: any;
   color: string;
}

type ExpandedTableRowProps = {
   row: Row<SubItem>;
   parentIndex: number;
   parentItem: Item;
   color: string;
   onClick: () => void;
   viewedItems: Set<number>;
};

const StyledInactiveWrapperDiv = styled.div<{ inactive: boolean }>`
   ${({ inactive }) => inactive && css`
      text-decoration: line-through;
   `}
`;

const StyledExpandedTableRow = styled(StyledTableRow)`
   :hover {
      background-color: ${theme.colors.bg.secondary};
   }
`;

const StyledPositionedIcon = styled(SSRIcon)`
   height: 1.2rem;
   left: 1.4rem;
   position: absolute;
`;

// eslint-disable-next-line @typescript-eslint/no-unused-vars
const IconBase = ({ locked, ...props }: IconProps) => <SSRIcon {...props} />;

const StyleClientCreatedIcon = styled(IconBase)`
   height: 1.2rem;
   position: absolute;
   left: 1.4rem;
   ${props => props.locked && css`
      left: -3rem;
   `}
`;

export const ExpandedTableRow = ({ row, parentItem, color, onClick }: ExpandedTableRowProps) => {
   const [hovering, setHovering] = useState(false);
   const [disabledHovering, setDisabledHovering] = useState(false);

   const bind = useHover(({ hovering }) => {
      if (!disabledHovering) setHovering(!!hovering);
   });

   const rowRef = useRef<HTMLTableRowElement>(null);

   return (
      <StyledExpandedTableRow {...(bind() as object)} key={row.id} ref={rowRef} onClick={onClick}>
         {row.getVisibleCells().map((cell) => {
            const firstColumn = row.getVisibleCells().indexOf(cell) === 0;
            return (
               <StyledTableData
                  style={{
                     color: color,
                     textAlign: cell.column.columnDef.meta?.rightAligned ? 'right' : 'left'
                  }}
                  key={cell.id}>
                  <StyledTableDataDiv
                     key={cell.id}
                     style={{
                        justifyContent: cell.column.columnDef.meta?.rightAligned ? 'flex-end' : 'flex-start',
                        width: '100%'
                     }}>
                     {row.original.clientCreated && firstColumn && (
                        <StyleClientCreatedIcon locked={row.original.locked} paths={handIcon} color={theme.colors.text.secondary} />
                     )}
                     {row.original.locked && firstColumn && <StyledPositionedIcon paths={lockIcon} color={theme.colors.text.secondary} />}

                     <StyledInactiveWrapperDiv inactive={!row.original.active}>
                        {flexRender(cell.column.columnDef.cell, cell.getContext())}
                     </StyledInactiveWrapperDiv>

                     {!parentItem.deleted && (
                        <ExpandedRowActions
                           hovering={hovering}
                           setHovering={setHovering}
                           setDisabledHovering={setDisabledHovering}
                           rowRef={rowRef}
                           rowData={row.original}
                           parentItem={parentItem}
                        />
                     )}
                  </StyledTableDataDiv>
               </StyledTableData>
            );
         })}
      </StyledExpandedTableRow>
   );
};
